/**
 * useCitationSource — fetch the document behind a citation and hand back a
 * URL the viewer can render.
 *
 * The bytes come from `POST /research/chat-groups/{uuid}/stream-source`, keyed
 * on the citation's `blob_path` alone. A 200 is the raw file, so it becomes an
 * object URL and goes straight to the existing `CitationDocumentViewer`.
 *
 * Two rules the endpoint's contract imposes:
 *
 *  • A citation with an empty `blob_path` has NO source. That is a legitimate
 *    state, not an error — `canOpen` is false and the caller must not offer
 *    the action at all, rather than offering one that fails.
 *  • Blob-not-found is 400, not 404. There is nothing useful to distinguish,
 *    so every non-200 collapses to one "couldn't load this source" state.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../store';
import { researchStreamSource } from '../../store/actions/ChatAction';
import { streamMatterSource } from '../../store/actions/Matters/MatterActions';
import { ResearchCitation } from './types';
import {
  SourceKind,
  contentTypeForPath,
  detectSourceKind,
  readAsUtf8,
} from './sourceDocument';
import { LocatorTarget, citationTarget, locatorTargets } from './citationMarkers';

/**
 * Recover a citation's storage path from the run's retrieval records.
 *
 * The engine's SYNTHESIS citations frequently carry no `blob_path` — that is
 * the reason clicking a found corpus law fired no request at all. The
 * RETRIEVAL records for the same run describe the documents that were
 * actually fetched, and those do carry a path. Matching the two recovers a
 * streamable handle without waiting on the engine.
 *
 * Matching is by document uuid where both sides have one, then by exact
 * normalised title. Deliberately NOT the citation panel's fuzzy
 * fragment-overlap match: that is fine for choosing an excerpt to display,
 * but opening the WRONG document is worse than opening none.
 */
export function retrievalSourcePath(
  citation: ResearchCitation | null | undefined,
  retrievalData?: Record<string, any> | null
): { blobPath: string; container: string } | null {
  if (!citation || !retrievalData) return null;

  const wantedUuid = String(citation.document_uuid || '').trim();
  const wantedTitle = String(citation.title || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!wantedUuid && !wantedTitle) return null;

  for (const source of Object.values(retrievalData)) {
    const records: any[] = (source as any)?.citations || [];
    for (const record of records) {
      const path = String(
        record?.blob_path || record?.path || record?.source_path || ''
      ).trim();
      if (!path) continue;

      const uuid = String(record?.document_uuid || record?.id || '').trim();
      const title = String(record?.title || '').trim().toLowerCase().replace(/\s+/g, ' ');

      if ((wantedUuid && uuid === wantedUuid) || (wantedTitle && title === wantedTitle)) {
        return { blobPath: path, container: String(record?.container || '').trim() };
      }
    }
  }
  return null;
}

/**
 * Does this citation have a document we can stream?
 *
 * `retrievalData` is consulted as a fallback, so a citation the engine left
 * without a path is still openable when the run's retrieval records name one.
 */
export function canOpenSource(
  citation: ResearchCitation | null | undefined,
  retrievalData?: Record<string, any> | null
): boolean {
  if (!citation) return false;
  if (typeof citation.blob_path === 'string' && citation.blob_path.trim() !== '') return true;
  return retrievalSourcePath(citation, retrievalData) !== null;
}

export interface UseCitationSourceResult {
  /** Object URL for the streamed bytes, or null. */
  url: string | null;
  contentType: string;
  /** What the bytes are, so the viewer picks the right branch. */
  kind: SourceKind | null;
  /** The decoded document — UTF-8, whatever the blob claimed. */
  text: string;
  /** Where in the document the cited passage is — best target first. */
  locators: LocatorTarget[];
  isLoading: boolean;
  /** The fetch completed without bytes — show "couldn't load this source". */
  failed: boolean;
  open: (citation: ResearchCitation, retrievalData?: Record<string, any> | null) => void;
  clear: () => void;
}

/**
 * Which endpoint streams the bytes.
 *
 * ONE code path, two doors. The research surface streams through its chat
 * (`/research/chat-groups/<uuid>/stream-source`), a matter through its own
 * (`/matters/stream-source`), and both take the citation's `blob_path` and
 * `container` verbatim. Everything after the fetch — decoding, kind detection,
 * the locator chain that finds the quote — is the same work, so it is written
 * once and the surface only chooses the door.
 */
export type CitationSurface = 'research' | 'matter';

export function useCitationSource(
  chatUuid: string | null,
  surface: CitationSurface = 'research'
): UseCitationSourceResult {
  const dispatch = useAppDispatch();
  const [url, setUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState('');
  const [kind, setKind] = useState<SourceKind | null>(null);
  const [text, setText] = useState('');
  const [locators, setLocators] = useState<LocatorTarget[]>([{ kind: 'top' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // Every object URL we mint has to be revoked, or the blob leaks for the
  // lifetime of the page.
  const urlRef = useRef<string | null>(null);
  const requestRef = useRef(0);

  const revoke = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  useEffect(() => revoke, [revoke]);

  const clear = useCallback(() => {
    revoke();
    setUrl(null);
    setContentType('');
    setKind(null);
    setText('');
    setLocators([{ kind: 'top' }]);
    setFailed(false);
    setIsLoading(false);
  }, [revoke]);

  const open = useCallback((
    citation: ResearchCitation,
    retrievalData?: Record<string, any> | null
  ) => {
    // A matter streams against the matter, not against a chat, so it has no
    // chat uuid to require.
    if (surface === 'research' && !chatUuid) return;
    const target = citationTarget(citation);

    // The citation's own path when it has one, otherwise whatever the run's
    // retrieval records know about the same document.
    const recovered = target.canStream ? null : retrievalSourcePath(citation, retrievalData);
    const blobPath = target.blobPath || recovered?.blobPath || '';
    const container = target.container || recovered?.container || '';
    if (!blobPath) return;

    const request = ++requestRef.current;
    /*
      Where in the document to land — the WHOLE chain, not one target.

      The viewer walks it and keeps the first form that actually finds the
      passage in the rendered document. That matters because the strongest
      target on paper is routinely the weakest in practice: offsets are taken
      over extracted text and go stale (or land at 0) when chunks are merged,
      while the verbatim quote can simply be searched for in what the reader
      can see. `citation.quote` backs the locator up when the locator itself
      carries no quote of its own.
    */
    setLocators(locatorTargets(citation.locator, target.url, citation.quote || ''));
    revoke();
    setUrl(null);
    setFailed(false);
    setIsLoading(true);

    // Derived from the path. Asking for `text/html` on a PDF mislabels the
    // response and is why some sources came back unrenderable.
    const contentType = contentTypeForPath(blobPath, (citation as any).content_type);

    const stream: Promise<{ blob: Blob; contentType: string } | null> =
      surface === 'matter'
        ? (dispatch(streamMatterSource(blobPath, 'inline', contentType, container || undefined)) as any)
            .then((result: any) =>
              result?.success && result.blob
                ? { blob: result.blob, contentType: result.contentType || contentType }
                : null
            )
        : (dispatch(researchStreamSource(chatUuid as string, blobPath, contentType, container)) as any);

    stream
      .then(async (result) => {
        // A citation opened after this one supersedes it.
        if (request !== requestRef.current) return;
        if (!result) {
          setFailed(true);
          return;
        }

        // The whole document is assembled before anything renders: half an
        // HTML document shown as text is markup on screen, and re-rendering
        // it as it completes would flicker.
        const decoded = await readAsUtf8(result.blob);
        if (request !== requestRef.current) return;

        const detected = detectSourceKind(result.contentType, decoded);
        if (detected === 'empty' || detected === 'binary') {
          setKind(detected);
          setContentType(result.contentType);
          return;
        }

        const objectUrl = URL.createObjectURL(result.blob);
        urlRef.current = objectUrl;
        setUrl(objectUrl);
        setContentType(result.contentType);
        setKind(detected);
        setText(decoded);
      })
      .finally(() => {
        if (request === requestRef.current) setIsLoading(false);
      });
  }, [chatUuid, surface, dispatch, revoke]);

  return { url, contentType, kind, text, locators, isLoading, failed, open, clear };
}

export default useCitationSource;
